/**
 * ProfileContext - Centralized Profile Management Context
 *
 * Provides a single source of truth for user profile data across the entire application.
 * Prevents multiple components from making duplicate API calls and ensures consistent
 * profile state management.
 *
 * @author By John Bellew - Infinia.ie - last updated 29/09/2025
 */

import React, { createContext, useContext, useEffect, useRef, useMemo } from "react";
import { useProfileStatus } from "../hooks/useProfileStatus";
import { pushUserData, loadGTM } from "../utils/gtm";
import errorLogger from "../utils/errorLogger";

const GTM_CONTAINER_ID = "GTM-PDWDG8M";

// Create the context
const ProfileContext = createContext();

/**
 * ProfileProvider Component
 *
 * Wraps the application and provides centralized profile management.
 * Uses the useProfileStatus hook internally and shares the data with all child components.
 */
export const ProfileProvider = ({ children }) => {
  const { profileStatus, loading, profile, dismissWarning, showWarning } = useProfileStatus();

  const gtmInitializedRef = useRef(false);

  useEffect(() => {
    if (loading) return;

    if(profile == null) {
      return;
    }

    errorLogger.setUserEmail(profile.email || profile.mail);

    const hasRequiredData = profile.memberCohort?.trim() &&
      (profile.NoOfYrsintheIndustry !== null &&
       profile.NoOfYrsintheIndustry !== undefined &&
       String(profile.NoOfYrsintheIndustry).trim() !== '')
    if (hasRequiredData) {
      pushUserData(profile);
    }

    if (!gtmInitializedRef.current) {
      loadGTM(GTM_CONTAINER_ID);
      gtmInitializedRef.current = true;
    }
  }, [profile?.memberCohort, profile?.NoOfYrsintheIndustry, loading]);

  const memoizedValue = useMemo(
    () => ({ profileStatus, loading, profile, dismissWarning, showWarning }),
    [profileStatus, loading, profile, dismissWarning, showWarning]
  );

  return (
    <ProfileContext.Provider value={memoizedValue}>
      {children}
    </ProfileContext.Provider>
  );
};

/**
 * useProfile Hook
 *
 * Custom hook to access profile data from context.
 * Replaces direct usage of useProfileStatus in components.
 *
 * @returns {Object} Profile data and management functions
 */
export const useProfile = () => {
  const context = useContext(ProfileContext);

  if (context === undefined) {
    throw new Error("useProfile must be used within a ProfileProvider");
  }

  return context;
};

export default ProfileContext;
